import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { servicesAPI, bookingsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './ServiceDetail.css';

const timeSlots = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'];

const ServiceDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState({ bookingDate: '', timeSlot: '', address: '', city: '', notes: '' });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    servicesAPI.getOne(id).then(res => { setService(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  const handleBook = async (e) => {
    e.preventDefault();
    if (!user) return navigate('/login');
    setBookingLoading(true);
    setError('');
    try {
      await bookingsAPI.create({ ...booking, service: id, totalAmount: service.price });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Booking failed. Please try again.');
    }
    setBookingLoading(false);
  };

  if (loading) return <div className="detail-loading"><div className="spinner"></div><p>Loading service...</p></div>;
  if (!service) return <div className="detail-loading"><p>Service not found.</p></div>;

  const commission = service.price * 0.15;
  const providerEarning = service.price - commission;

  return (
    <div className="detail-page">
      <div className="detail-container">
        {/* Left: Service Info */}
        <div className="detail-main">
          <button className="back-btn" onClick={() => navigate(-1)}>← Back to Services</button>

          <div className="detail-card">
            <div className="detail-header">
              <div className="detail-avatar">{service.provider?.name?.charAt(0) || 'P'}</div>
              <div className="detail-info">
                <div className="detail-badge">{service.category}</div>
                <h1>{service.title}</h1>
                <div className="detail-meta-row">
                  <span>👤 {service.provider?.name}</span>
                  <span>📍 {service.city}</span>
                  <span>⭐ {service.rating?.toFixed(1) || 'New'} ({service.totalReviews || 0} reviews)</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3>About This Service</h3>
              <p>{service.description}</p>
            </div>

            <div className="detail-section">
              <h3>Availability</h3>
              <div className="avail-pills">
                {service.availability?.map(day => <span key={day} className="avail-pill">{day}</span>)}
              </div>
            </div>

            <div className="detail-section">
              <h3>Pricing Breakdown</h3>
              <div className="pricing-table">
                <div className="pricing-row">
                  <span>Service Price</span>
                  <span>Rs. {service.price}</span>
                </div>
                <div className="pricing-row">
                  <span>Platform Fee (15%)</span>
                  <span>Rs. {commission.toFixed(0)}</span>
                </div>
                <div className="pricing-row total">
                  <span>You Pay</span>
                  <span>Rs. {service.price}</span>
                </div>
                <div className="pricing-row provider">
                  <span>Provider Earns</span>
                  <span>Rs. {providerEarning.toFixed(0)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Booking Form */}
        <div className="booking-sidebar">
          <div className="booking-card">
            <div className="booking-price">
              Rs. {service.price}
              <span> / {service.priceType?.replace('_', ' ')}</span>
            </div>

            {success ? (
              <div className="booking-success">
                <div className="success-icon">✅</div>
                <h3>Booking Confirmed!</h3>
                <p>Your booking has been placed successfully. The provider will contact you shortly.</p>
                <button onClick={() => navigate('/dashboard')} className="view-bookings-btn">View My Bookings</button>
              </div>
            ) : (
              <form onSubmit={handleBook}>
                <h3>Book This Service</h3>

                {error && <div className="booking-error">⚠️ {error}</div>}

                <div className="booking-form-group">
                  <label>Select Date</label>
                  <input
                    type="date"
                    value={booking.bookingDate}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setBooking({ ...booking, bookingDate: e.target.value })}
                    required
                  />
                </div>

                <div className="booking-form-group">
                  <label>Select Time Slot</label>
                  <div className="time-slots">
                    {timeSlots.map(slot => (
                      <button
                        key={slot}
                        type="button"
                        className={`time-slot ${booking.timeSlot === slot ? 'active' : ''}`}
                        onClick={() => setBooking({ ...booking, timeSlot: slot })}
                      >{slot}</button>
                    ))}
                  </div>
                </div>

                <div className="booking-form-group">
                  <label>Your Address</label>
                  <input
                    type="text"
                    placeholder="Street address, area..."
                    value={booking.address}
                    onChange={(e) => setBooking({ ...booking, address: e.target.value })}
                    required
                  />
                </div>

                <div className="booking-form-group">
                  <label>City</label>
                  <select value={booking.city} onChange={(e) => setBooking({ ...booking, city: e.target.value })} required>
                    <option value="">Select City</option>
                   {['Islamabad', 'Rawalpindi', 'Lahore', 'Karachi', 'Peshawar', 'Multan', 'Faisalabad'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="booking-form-group">
                  <label>Additional Notes (optional)</label>
                  <textarea
                    placeholder="Any specific instructions for the provider..."
                    value={booking.notes}
                    onChange={(e) => setBooking({ ...booking, notes: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="booking-summary">
                  <div className="bs-row"><span>Service Price</span><span>Rs. {service.price}</span></div>
                  <div className="bs-row total"><span>Total Amount</span><span>Rs. {service.price}</span></div>
                </div>

                <button type="submit" className="book-now-btn" disabled={bookingLoading || !booking.timeSlot}>
                  {bookingLoading ? 'Processing...' : user ? 'Confirm Booking' : 'Login to Book'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
